import React from 'react';
import { AlertCircle, XCircle, RefreshCw } from 'lucide-react';
import { AppError, ErrorCode, getErrorConfig, isCriticalError } from '@/types/errors';

interface ErrorMessageOverlayProps {
  error: AppError | null;
  onDismissCallbacks?: Partial<Record<ErrorCode, () => void>>;
  retryCallbacks?: Partial<Record<ErrorCode, () => void>>;
}

/**
 * Renders an error message overlay with optional retry and dismiss actions.
 *
 * for DISCRETE errors
 *
 */
export function ErrorMessageOverlay({
  error,
  onDismissCallbacks,
  retryCallbacks
}: ErrorMessageOverlayProps) {
  if (!error) return null;

  // Get error configuration
  const errorConfig = getErrorConfig(error.code);
  const critical = isCriticalError(error.code);
  const handleRetry = retryCallbacks?.[error.code];
  const onDismiss = onDismissCallbacks?.[error.code];

  if (!errorConfig.shouldDisplay) return null;

  // Determine error severity and icon based on error code
  const ErrorIcon = critical ? XCircle : AlertCircle;
  const iconColor = critical ? 'text-red-500' : 'text-yellow-500';

  // Format error code for display (snake_case -> Title Case) - fallback if no title
  const formatErrorCode = (code: string) => {
    return code
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Use configured title or fall back to formatted error code
  const displayTitle = errorConfig.title || formatErrorCode(error.code);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center">
      <div className="bg-[#1E293B]/90 border border-slate-700 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex flex-col items-center text-center">
          <ErrorIcon className={`h-12 w-12 ${iconColor} mb-4`}/>

          <h3 className="text-xl font-semibold text-white mb-2">
            {displayTitle}
          </h3>

          <p className="text-slate-300 mb-2">
            {errorConfig.message}
          </p>
          {/* Action buttons */}
          <div className="mt-6 flex gap-3 w-full">
            {handleRetry && (
              <button
                onClick={handleRetry}
                className="flex-1 px-4 py-2 bg-[#0E9BB5] hover:bg-[#0A8399] text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4"/>
                Try Again
              </button>
            )}
            {onDismiss && (
              <button
                onClick={onDismiss}
                className={`${handleRetry ? 'flex-1' : 'w-full'} px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors`}
              >
                {handleRetry ? 'Cancel' : 'Dismiss'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
